import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { updateUserPreferences } from '@/lib/supabase/user';
import { Loader2 } from 'lucide-react';

const CURRENCIES = [
  { value: 'GBP', label: 'British Pound (£)' },
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'AUD', label: 'Australian Dollar (A$)' },
  { value: 'CAD', label: 'Canadian Dollar (C$)' },
  { value: 'CHF', label: 'Swiss Franc (Fr)' },
  { value: 'JPY', label: 'Japanese Yen (¥)' },
  { value: 'NZD', label: 'New Zealand Dollar (NZ$)' },
  { value: 'SGD', label: 'Singapore Dollar (S$)' },
  { value: 'HKD', label: 'Hong Kong Dollar (HK$)' },
] as const;

export function PreferenceSettings() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    timezone: 'UTC',
    dateFormat: 'DD/MM/YYYY',
    currency: 'GBP',
  });

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateUserPreferences(preferences);
      toast({
        title: 'Success',
        description: 'Your preferences have been updated',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update preferences',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Preferences</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Time Zone</Label>
            <Select
              value={preferences.timezone}
              onValueChange={(value) => setPreferences(prev => ({ ...prev, timezone: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UTC">UTC</SelectItem>
                <SelectItem value="GMT">GMT (London)</SelectItem>
                <SelectItem value="EST">EST (New York)</SelectItem>
                <SelectItem value="PST">PST (Los Angeles)</SelectItem>
                <SelectItem value="AEST">AEST (Sydney)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Date Format</Label>
            <Select
              value={preferences.dateFormat}
              onValueChange={(value) => setPreferences(prev => ({ ...prev, dateFormat: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select date format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Currency</Label>
            <Select
              value={preferences.currency}
              onValueChange={(value) => setPreferences(prev => ({ ...prev, currency: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={handleSave} disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Preferences
        </Button>
      </CardContent>
    </Card>
  );
}